// Pure drop resolver for the calendar drag-and-drop (pointer-based via @dnd-kit).
//
// This is the ONE unit-testable seam of the DnD migration: given the active
// drag payload, the droppable it was released over, and today's date, decide
// exactly what should happen — or why nothing should (a named reason). The
// wiring around it (data carried on draggables, ids on droppables, dispatch in
// the hook) is covered by manual testing; this function makes the *decision*
// deterministic and asserted.
//
// Branch order mirrors the old native handlers (past-day guard before payload
// branching, so every kind inherits the no-past-scheduling rule).

import type { AssignmentPurpose } from '@gutterinv/shared';
import type { CalendarDragPayload } from './calendar-dates.js';
import { addDaysISO } from './calendar-dates.js';
import { taskDropReassignTo } from './calendar-tasks.js';

/** Where a drag was released. Day cells (month) + person-day cells (week) carry
 *  an `iso` for the past-day guard; the sidebar carries none (unassign zone). */
export type DropTarget =
  | { kind: 'day'; iso: string }
  | { kind: 'person-day'; iso: string; personId: string; personName: string; isNoPeopleRow: boolean }
  | { kind: 'sidebar' };

/** API-input-level outcome. The hook maps each to the matching mutation; the
 *  optimistic placeholder's extra display fields (job details, person name) are
 *  enriched in the hook from active/over data, not here. */
export type DropAction =
  | { action: 'reassign-dates'; assignmentId: string; startDate: string; endDate: string }
  | { action: 'task-move'; taskId: string; taskDate: string; people?: { id: string; name: string }[] }
  | { action: 'new-assignment'; jobId: string; purpose: AssignmentPurpose; personIds: string[]; startDate: string; endDate: string }
  | { action: 'crew-picker'; jobId: string; iso: string; purpose: AssignmentPurpose; durationDays: number }
  | { action: 'unassign'; assignmentId: string };

export type DropReason = 'no-target' | 'past-day' | 'sidebar-non-reassign' | 'no-people-row';
export type DropResult = DropAction | { action: null; reason: DropReason };

export function resolveCalendarDrop(
  payload: CalendarDragPayload,
  over: DropTarget | null,
  todayISO: string,
): DropResult {
  if (!over) return { action: null, reason: 'no-target' };

  // Sidebar = unassign zone, accepts ONLY an existing assignment chip.
  if (over.kind === 'sidebar') {
    if (payload.kind === 'reassign') return { action: 'unassign', assignmentId: payload.oldAssignmentId };
    return { action: null, reason: 'sidebar-non-reassign' };
  }

  // Past-day guard FIRST — matches the create/edit date-floor everywhere else.
  if (over.iso < todayISO) return { action: null, reason: 'past-day' };
  const iso = over.iso;

  if (payload.kind === 'reassign') {
    const endDate = payload.durationDays > 0 ? addDaysISO(iso, payload.durationDays) : iso;
    return { action: 'reassign-dates', assignmentId: payload.oldAssignmentId, startDate: iso, endDate };
  }

  if (payload.kind === 'task-reassign') {
    // Week person-day: a single-assignee task dropped on a different real person
    // row reassigns to them; everything else is a date-only move. Month day: always date-only.
    if (over.kind === 'person-day') {
      const reassignTo = taskDropReassignTo(payload.personIds, over.personId, over.isNoPeopleRow);
      if (reassignTo) {
        return { action: 'task-move', taskId: payload.taskId, taskDate: iso, people: [{ id: over.personId, name: over.personName }] };
      }
    }
    return { action: 'task-move', taskId: payload.taskId, taskDate: iso };
  }

  // payload.kind === 'unassigned' — a sidebar job dropped onto the calendar.
  if (over.kind === 'day') {
    // Month: no per-row crew context → raise the crew picker. durationDays is
    // always 0 for an unassigned drop (single new assignment).
    return { action: 'crew-picker', jobId: payload.jobId, iso, purpose: payload.purpose, durationDays: 0 };
  }
  // Week person-day: assign directly to that person, except the synthetic row.
  if (over.isNoPeopleRow) return { action: null, reason: 'no-people-row' };
  return { action: 'new-assignment', jobId: payload.jobId, purpose: payload.purpose, personIds: [over.personId], startDate: iso, endDate: iso };
}
